'use client';

import type { FormData } from '../FormProject';

type Props = {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
};

const SIZES = [
  'Petit (< 5cm)',
  'Moyen (5-15cm)',
  'Grand (15-30cm)',
  'Très grand (> 30cm)',
  'Je ne sais pas',
];

const BODY_PLACEMENTS = [
  'Avant-bras',
  'Bras (biceps)',
  'Épaule',
  'Poitrine',
  'Dos',
  'Côtes',
  'Cuisse',
  'Mollet',
  'Cheville',
  'Pied',
  'Main',
  'Doigt',
  'Nuque',
  'Cou',
  'Autre',
];

export default function Step1Project({ formData, updateFormData }: Props) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Parle-moi de ton projet</h2>
        <p className="mt-2 text-gray-600">Décris ton idée de tatouage et le style qui te plaît</p>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description du projet <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          rows={4}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
          placeholder="Décris ton idée de tatouage, les éléments que tu souhaites inclure, la signification..."
          value={formData.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
        />
      </div>

      {/* Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Taille approximative <span className="text-red-500">*</span>
        </label>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SIZES.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => updateFormData({ size })}
              className={`rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all ${
                formData.size === size
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900">Emplacement du tatouage</h2>
        <p className="mt-2 text-gray-600">Où souhaitez-vous placer votre tatouage ?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Zone du corps <span className="text-red-500">*</span>
        </label>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {BODY_PLACEMENTS.map((placement) => (
            <button
              key={placement}
              type="button"
              onClick={() => updateFormData({ bodyPlacement: placement })}
              className={`rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all ${
                formData.bodyPlacement === placement
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300'
              }`}
            >
              {placement}
            </button>
          ))}
        </div>
      </div>

      {/* Custom placement if "Autre" */}
      {formData.bodyPlacement === 'Autre' && (
        <div>
          <label htmlFor="customPlacement" className="block text-sm font-medium text-gray-700">
            Précisez l'emplacement
          </label>
          <input
            type="text"
            id="customPlacement"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
            placeholder="Où souhaitez-vous placer votre tatouage ?"
            value={formData.customPlacement}
            onChange={(e) => updateFormData({ customPlacement: e.target.value })}
          />
        </div>
      )}

      {/* Visual representation (optional enhancement) */}
      <div className="rounded-lg bg-emerald-50 p-6">
        <h3 className="font-semibold text-emerald-900">💡 Conseil</h3>
        <p className="mt-2 text-sm text-emerald-800">
          Certains emplacements sont plus sensibles que d'autres. Nous pourrons discuter ensemble du
          meilleur emplacement selon votre projet et votre tolérance à la douleur.
        </p>
      </div>
    </div>
  );
}
